import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  QrCode,
  Building2,
  Shield,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Dumbbell,
  Package,
  ShoppingCart,
  Calendar,
  XCircle,
  Zap,
  MessageSquare,
  UserCog,
  Apple,
  Gauge,
  Gift,
  Receipt
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useState } from 'react';

interface SidebarLink {
  label: string;
  href: string;
  icon: React.ElementType;
  roles: string[];
  module?: string;
}

const links: SidebarLink[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['super_admin', 'gym_admin', 'gym_supervisor', 'gym_staff', 'staff'] },
  { label: 'Gimnasios', href: '/gyms', icon: Building2, roles: ['super_admin'] },
  { label: 'Usuarios', href: '/users', icon: Shield, roles: ['super_admin', 'gym_admin'] },
  { label: 'Empleados', href: '/employees', icon: UserCog, roles: ['gym_admin', 'gym_supervisor', 'gym_staff', 'staff'], module: 'employees' },
  { label: 'Clientes', href: '/clients', icon: Users, roles: ['gym_admin', 'gym_supervisor', 'gym_staff', 'staff'], module: 'clients' },
  { label: 'Nutrición', href: '/nutrition', icon: Apple, roles: ['gym_admin', 'gym_supervisor', 'gym_staff', 'staff'], module: 'nutricion' },
  { label: 'Membresías', href: '/memberships', icon: CreditCard, roles: ['gym_admin', 'gym_supervisor', 'gym_staff', 'staff'], module: 'memberships' },
  { label: 'Scanner QR', href: '/scanner', icon: QrCode, roles: ['gym_admin', 'gym_supervisor', 'gym_staff', 'staff'], module: 'scanner' },
  { label: 'Inventario', href: '/inventory', icon: Package, roles: ['gym_admin', 'gym_supervisor'], module: 'inventory' },
  { label: 'Ventas', href: '/sales', icon: ShoppingCart, roles: ['gym_admin', 'gym_supervisor', 'gym_staff', 'staff'], module: 'sales' },
  { label: 'Gastos', href: '/expenses', icon: FileText, roles: ['gym_admin', 'gym_supervisor'], module: 'expenses' },
  { label: 'Recompensas', href: '/rewards', icon: Gift, roles: ['gym_admin', 'gym_supervisor'], module: 'rewards' },
  { label: 'Facturación', href: '/facturacion', icon: Receipt, roles: ['gym_admin', 'gym_supervisor'], module: 'facturacion' },
  { label: 'Clases', href: '/classes', icon: Calendar, roles: ['gym_admin', 'gym_supervisor', 'gym_staff', 'staff'], module: 'classes' },
  { label: 'Reportes', href: '/reports', icon: FileText, roles: ['gym_admin', 'gym_supervisor'], module: 'reports' },
  { label: 'Configuración', href: '/settings', icon: Settings, roles: ['super_admin', 'gym_admin'] },
  { label: 'WhatsApp AI', href: '/whatsapp', icon: MessageSquare, roles: ['super_admin', 'gym_admin', 'gym_staff', 'staff'], module: 'webhooks' },
  { label: 'Webhooks', href: '/webhooks', icon: Zap, roles: ['super_admin'] },
  { label: 'Uso del Plan', href: '/usage', icon: Gauge, roles: ['super_admin'] },
  { label: 'Soporte', href: '/support', icon: MessageSquare, roles: ['super_admin', 'gym_admin', 'gym_supervisor', 'gym_staff', 'staff'] },
];

interface SidebarProps {
  onClose?: () => void;
}

export function Sidebar({ onClose }: SidebarProps) {
  const location = useLocation();
  const { user, gym, hasRole, hasModule, logout, setSelectedGym } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const filteredLinks = links.filter(link => {
    // Basic role check
    const roleAllowed = link.roles.some(role => hasRole([role as any]));
    if (!roleAllowed) return false;

    // Module check (if applicable)
    const moduleAllowed = !link.module || hasModule(link.module);
    if (!moduleAllowed) return false;

    // Scope check: If it's a gym-specific module (requires a gym context)
    // and we are a super_admin without a selected gym, hide it.
    // Exceptions: Dashboard, Gimnasios, Usuarios, Settings are "Global" or handle no-gym state.
    const globalLinks = ['/dashboard', '/gyms', '/users', '/settings', '/webhooks', '/usage', '/support'];
    const isGlobalLink = globalLinks.includes(link.href);

    if (user?.role === 'super_admin' && !gym && !isGlobalLink) {
      return false;
    }

    return true;
  });

  const handleLinkClick = () => {
    if (onClose) onClose();
  };

  return (
    <aside
      className={cn(
        "h-full border-r border-sidebar-border bg-sidebar transition-all duration-300",
        collapsed ? "w-16" : "w-64",
        "dark:bg-black/95 dark:backdrop-blur-xl"
      )}
    >
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
          {!collapsed && (
            <Link to="/dashboard" className="flex items-center gap-2" onClick={handleLinkClick}>
              <div className="flex h-8 w-8 items-center justify-center rounded-lg gradient-primary">
                <Dumbbell className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-sidebar-foreground">GymPro SV</span>
            </Link>
          )}
          {collapsed && (
            <div className="flex h-8 w-8 items-center justify-center rounded-lg gradient-primary mx-auto">
              <Dumbbell className="h-5 w-5 text-primary-foreground" />
            </div>
          )}
          {/* Mobile Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onClose}
          >
            <XCircle className="h-5 w-5" />
          </Button>
        </div>

        {/* Gym Info */}
        {!collapsed && gym && (
          <div className="border-b border-sidebar-border p-4 group">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">Gimnasio actual</p>
              {hasRole(['super_admin']) && (
                <button
                  onClick={() => setSelectedGym(null)}
                  className="text-[10px] text-destructive opacity-0 group-hover:opacity-100 transition-opacity hover:underline font-bold"
                >
                  LIMPIAR
                </button>
              )}
            </div>
            <div className="flex flex-col gap-0.5 overflow-hidden">
              <div className="flex items-center gap-2">
                <p className="font-medium text-sidebar-foreground truncate text-sm leading-tight">{gym.name}</p>
                {gym.status !== 'active' && (
                  <span className={cn(
                    "px-1.5 py-0.5 rounded-full text-[8px] font-black uppercase shrink-0",
                    gym.status === 'pending' ? "bg-amber-100 text-amber-600" : "bg-red-100 text-red-600"
                  )}>
                    {gym.status === 'pending' ? 'PEND' : 'EXP'}
                  </span>
                )}
              </div>
              {gym.expiry_date && (
                <p className={cn(
                  "text-[10px] font-bold transition-colors",
                  (() => {
                    const diff = new Date(gym.expiry_date).getTime() - new Date().getTime();
                    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
                    if (days <= 0) return "text-red-500";
                    if (days <= 7) return "text-orange-500 animate-pulse";
                    return "text-muted-foreground/60";
                  })()
                )}>
                  Próximo pago: {new Date(gym.expiry_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-2">
          {filteredLinks.map((link) => {
            const isActive = location.pathname === link.href;
            const Icon = link.icon;

            return (
              <Link
                key={link.href}
                to={link.href}
                onClick={handleLinkClick}
                className={cn(
                  "flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-bold transition-all duration-300",
                  isActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-lg shadow-primary/20"
                    : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground",
                  collapsed && "justify-center px-2"
                )}
              >
                <Icon className={cn("h-5 w-5 shrink-0 transition-transform duration-300", isActive ? "text-primary scale-110" : "opacity-70 group-hover:opacity-100")} />
                {!collapsed && <span>{link.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-sidebar-border p-2">
          {!collapsed && user && (
            <div className="mb-2 rounded-lg bg-sidebar-accent/50 p-3">
              <p className="text-sm font-medium text-sidebar-foreground truncate">{user.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
          )}

          <div className={cn("flex items-center gap-2", collapsed && "flex-col")}>
            <ThemeToggle className="flex-shrink-0" />
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="flex-shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <LogOut className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCollapsed(!collapsed)}
              className="hidden lg:flex flex-shrink-0 ml-auto"
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </aside>
  );
}
